import React, { useState, useEffect, useRef } from 'react';
import { observer } from 'mobx-react';
import ReactJWPlayer from 'react-jw-player';
import { DecisionOverlay } from './Feedback/Desktop/decisionOverlay';
import { DecisionFeedback } from './Feedback/Desktop/decisionFeedback';
import DecisionPlayerProgressbar from './Feedback/DecisionPlayerProgressbar';
import StartFeedback from './Feedback/Desktop/StartFeedback';
import classes from 'classnames';
import Spinner from '../Spinner';
import { FlexDiv } from '../design-system/containers';
import StartFeedbackMobile from './Feedback/Mobile/StartFeedbackMobile';
import DecisionOverlayMobile from './Feedback/Mobile/DecisionOverlayMobile';
import { DecisionFeedbackMobile } from './Feedback/Mobile/DecisionFeedbackMobile';
import { useTranslation } from 'react-i18next';

const CONSTANTS = {
    EVENTS: {
        LOGIN: 1,
        LOGOUT: 2,
        SUBJECT_OPEN: 3,
        SUBJECT_CLOSE: 4,
        ANSWARE_CORRECT: 5,
        ANSWARE_INCORRECT: 6,
        VIDEO_START: 7,
        VIDEO_25: 8,
        VIDEO_50: 9,
        VIDEO_75: 10,
        VIDEO_95: 11,
        VIDEO_100: 12,
        CAMPAIGN_EMAIL: 13,
    },

    USERS_VIEW: {
        LISTS: 1,
        EDIT: 2,
        ADD_USERS: 3,
        LIST_USERS: 4,
        LIST_VIEW: 5,
    },
};

export const VideoPlayer = observer(
    ({
        subject,
        logEvent,
        videoPlayerUrl,
        token,
        videoTokenRequested,
        presetCaptionsLanguage,
        captionsSettings,
        onChange,
        enableAssessmentFinish,
        isAssessment,
        onLastDecision,
        resetDecisions,
        onFinishedWatchingAnswer,
        canFastForward = true,
    }) => {
        const { t } = useTranslation();

        const [videoDecisions, setVideoDecisions] = useState(null);
        const [showOverlay, setShowingOverlay] = useState(false);
        const [isAtEnd, setIsAtEnd] = useState(false);
        const [isPaused, setIsPaused] = useState(true);
        const [isReady, setIsReady] = useState(false);
        const [currentPlaylistIndex, setCurrentPlaylistIndex] = useState(0);
        const [showFeedback, setShowFeedback] = useState(false);
        const [showStartFeedback, setShowStartFeedback] = useState(false);
        const [isPlayingDecisionAnswer, setPlayingDecisionAnswer] =
            useState(false);
        const [counter, setProgress] = useState(0);
        const [playerError, setPlayerError] = useState(null);

        // Furthest position the user has genuinely watched (in seconds) and the
        // last reported position. Kept in refs so they are always read
        // synchronously inside the player's time handler (state batching would
        // otherwise lag and break forward-seek detection).
        const maxWatchedPositionRef = useRef(0);
        const lastPositionRef = useRef(0);
        // Percentage milestones we have already reported for the current
        // playlist item. Mirrors react-jw-player's internal `hasFired`, but is
        // driven by the genuinely-watched position instead of the raw (possibly
        // fast-forwarded) playhead position.
        const firedMilestonesRef = useRef({
            p25: false,
            p50: false,
            p75: false,
            p95: false,
            p100: false,
        });

        const resetWatchTracking = () => {
            maxWatchedPositionRef.current = 0;
            lastPositionRef.current = 0;
            firedMilestonesRef.current = {
                p25: false,
                p50: false,
                p75: false,
                p95: false,
                p100: false,
            };
        };

        /////////////////////////////////////////////////////////////
        /// Re-render functionality to fix JWPlayer not refreshing on subject change
        const [rerender, setRerender] = useState(true);
        useEffect(() => {
            setRerender(true);
            resetWatchTracking();
            setPlayerError(null);
        }, [subject, token && token.videoId]);

        useEffect(() => {
            if (rerender) {
                setRerender(false);
            }
        }, [rerender]);
        /////////////////////////////////////////////////////////////

        useEffect(() => {
            if (subject && subject.video_decisions) {
                if (!isAssessment) {
                    setShowFeedback(
                        isReady && !showStartFeedback && subject.hasBeenTried
                    );
                    setIsAtEnd(
                        !isPlayingDecisionAnswer && subject.hasBeenTried
                    );
                }
                setVideoDecisions(subject.video_decisions);
            }
        }, [subject, isReady, showStartFeedback]);

        useEffect(() => {
            const interval = setInterval(() => {
                setProgress((oldValue) => {
                    const newValue = isPaused ? oldValue : oldValue + 0.5;
                    return newValue;
                });
            }, 500);
            return () => clearInterval(interval);
        }, [isPaused]);

        const onError = (ev = {}) => {
            if (ev.code === 232403) {
                videoTokenRequested(subject.video_id_jw);
                return;
            }
            if (ev.code === 224003) {
                videoTokenRequested(subject.id);
                return;
            }
            setPlayerError(
                ev.message ||
                    t('Video could not be loaded. Please try again.')
            );
        };

        const onSetupError = (ev = {}) => {
            if (ev.code === 102403) {
                videoTokenRequested(subject.id);
                return;
            }
            if (ev.code === 224003) {
                videoTokenRequested(subject.id);
                return;
            }
            setPlayerError(
                ev.message ||
                    t('Video could not be loaded. Please try again.')
            );
        };

        const applyPlayerA11y = () => {
            try {
                const player = window.jwplayer();
                if (player && typeof player.getContainer === 'function') {
                    const container = player.getContainer();
                    if (container && subject && subject.title) {
                        container.setAttribute('aria-label', subject.title);
                    }
                }
                if (!document.getElementById('jw-a11y-focus-style')) {
                    const style = document.createElement('style');
                    style.id = 'jw-a11y-focus-style';
                    style.textContent = `
.jw-flag-tab-focus .jw-icon,
.jwplayer .jw-icon:focus-visible,
.jwplayer .jw-slider-time:focus-visible,
.jwplayer .jw-slider-volume:focus-visible {
    outline: 2px solid #fff !important;
    outline-offset: 2px !important;
}`;
                    document.head.appendChild(style);
                }
            } catch (e) {
                // player may not be ready
            }
        };

        const setCaptionsLanguage = () => {
            let captionsIndex = 0;
            if (presetCaptionsLanguage) {
                const captions = window.jwplayer().getCaptionsList();
                for (let i = 0; i < captions.length; i++) {
                    if (captions[i].label === presetCaptionsLanguage) {
                        captionsIndex = i;
                    }
                }
            }
            window.jwplayer().setCurrentCaptions(captionsIndex);
            hideNextUpPopupWindow();
        };

        const getPlayerCustomProps = () => {
            let props = { skin: { name: 'AwareGO' } };
            if (subject && subject.title) {
                props.title = subject.title;
            }
            if (captionsSettings) {
                props.captions = JSON.parse(captionsSettings);
            }
            return props;
        };

        const handleNinetyFivePercent = () => {
            if (subject && subject.video_decisions) {
                window.jwplayer().pause();
                setIsPaused(true);
                if (
                    !videoDecisions[window.jwplayer().getPlaylistIndex()]
                        .decision_1 ||
                    isPlayingDecisionAnswer
                ) {
                    //If this index points to a decision question without a decision, don't pause
                    setIsAtEnd(true);
                    if (isAssessment) {
                        //If it is an assessment decision, it ends here
                        enableAssessmentFinish();
                    } else {
                        //Showing the feedback modal (With 'Try Again' and 'See answer')
                        setShowFeedback(true);
                        if (isPlayingDecisionAnswer) {
                            onFinishedWatchingAnswer();
                            onLastDecision({ previewed: true });
                        } else {
                            onLastDecision();
                        }
                    }
                } else {
                    setIsAtEnd(false);
                }
                setShowingOverlay(true);
            }
        };

        const onAnswer = (decisionQuestionId, decision) => {
            setCurrentPlaylistIndex(decision.playlistIndex);
            setShowingOverlay(false);
            if (isAssessment) {
                onChange(decisionQuestionId, decision);
            } else {
                onChange(decision, currentPlaylistIndex);
            }
            window.jwplayer().playlistItem(decision.playlistIndex);
        };

        const hideNextUpPopupWindow = () => {
            const nextUpElement = window
                .jwplayer()
                .getContainer()
                .querySelector('.jw-nextup-container');
            if (nextUpElement) {
                nextUpElement.style.display = 'none';
            }
        };

        const tryAgainDecisionSubject = () => {
            setProgress(0);
            isPlayingDecisionAnswer && setPlayingDecisionAnswer(false);
            isPlayingDecisionAnswer && videoTokenRequested(subject.id);
            setIsAtEnd(false);
            setCurrentPlaylistIndex(0);
            setShowFeedback(false);
            setShowingOverlay(false);
            resetWatchTracking();
            window.jwplayer().playlistItem(0);
            resetDecisions();
        };

        const showAnswerVideo = () => {
            setProgress(0);
            videoTokenRequested(subject.answerSubjectId);
            setCurrentPlaylistIndex(0);
            setPlayingDecisionAnswer(true);
            setShowingOverlay(false);
            setIsAtEnd(false);
            resetWatchTracking();
            resetDecisions();
        };

        const handleSeek = () => {
            // We intentionally do nothing here; forward-seek prevention
            // is implemented in handleTime using position deltas.
        };

        // Fires the percentage milestone events (25/50/75/95) based on the
        // furthest *genuinely watched* position. We intentionally do NOT rely on
        // react-jw-player's own onTwentyFivePercent/.../onNinetyFivePercent
        // callbacks: those use the raw playhead position, so a (prevented)
        // forward seek momentarily reports a high position and fires the
        // milestone — showing the questionnaire and logging events — even though
        // the timeline is reverted. Worse, the library only fires each milestone
        // once, so it would never fire again once spuriously triggered.
        const reportWatchedMilestones = (watchedPosition, duration) => {
            if (!duration || duration <= 0) return;

            const watchedPercent = (watchedPosition / duration) * 100;
            const fired = firedMilestonesRef.current;

            if (!fired.p25 && watchedPercent > 25) {
                fired.p25 = true;
                logEvent(subject, CONSTANTS.EVENTS.VIDEO_25);
            }
            if (!fired.p50 && watchedPercent > 50) {
                fired.p50 = true;
                logEvent(subject, CONSTANTS.EVENTS.VIDEO_50);
            }
            if (!fired.p75 && watchedPercent > 75) {
                fired.p75 = true;
                logEvent(subject, CONSTANTS.EVENTS.VIDEO_75);
            }
            if (!fired.p95 && watchedPercent > 95) {
                fired.p95 = true;
                handleNinetyFivePercent();
                logEvent(subject, CONSTANTS.EVENTS.VIDEO_95);
            }
            if (!fired.p100 && watchedPercent >= 99.5) {
                fired.p100 = true;
                logEvent(subject, CONSTANTS.EVENTS.VIDEO_100);
            }
        };

        const handleTime = (event) => {
            const currentPosition = event.position;
            const duration = event.duration;
            const maxWatchedPosition = maxWatchedPositionRef.current;

            let newMaxWatchedPosition = maxWatchedPosition;

            if (!canFastForward) {
                const delta = currentPosition - lastPositionRef.current;

                // Treat large positive jumps as user-initiated forward seeks:
                // revert to the furthest genuinely watched position and do NOT
                // extend it, so milestones stay tied to real watch progress.
                if (delta > 1 && currentPosition > maxWatchedPosition) {
                    window.jwplayer().seek(maxWatchedPosition);
                } else if (currentPosition > maxWatchedPosition) {
                    // Normal forward playback: extend max watched position.
                    newMaxWatchedPosition = currentPosition;
                }
            } else {
                // When fast‑forward is allowed, keep maxWatchedPosition in sync
                // so that toggling the flag later still behaves sensibly.
                if (currentPosition > maxWatchedPosition) {
                    newMaxWatchedPosition = currentPosition;
                }
            }

            maxWatchedPositionRef.current = newMaxWatchedPosition;
            lastPositionRef.current = currentPosition;

            reportWatchedMilestones(newMaxWatchedPosition, duration);
        };

        const shouldShowQuestionOverlay = () => {
            return (
                videoDecisions &&
                showOverlay &&
                !showFeedback &&
                videoDecisions[currentPlaylistIndex] &&
                (videoDecisions[currentPlaylistIndex].questionText || isAtEnd)
            );
        };

        const startVideo = () => {
            window.jwplayer().play();
            setShowStartFeedback(false);
        };

        if (!token) {
            return (
                <div className="video">
                    <div
                        className="spinner-table small"
                        style={{ textAlign: 'center' }}
                    >
                        <Spinner />
                    </div>
                </div>
            );
        } else if (!token.videoId) {
            return (
                <div className="video" role="alert">
                    {subject.title} {t('video not available.')}
                </div>
            );
        } else if (playerError) {
            return (
                <div className="video" role="alert">
                    {playerError}
                </div>
            );
        } else {
            const playlist =
                token &&
                `https://content.jwplatform.com/feeds/${token.videoId}.json${token.sig_feed}`;

            const customProps = getPlayerCustomProps();

            return Modernizr.video ? (
                videoPlayerUrl ? (
                    <>
                        <FlexDiv column positionRelative>
                            {shouldShowQuestionOverlay() && (
                                <DecisionOverlay
                                    decisionQuestion={
                                        videoDecisions[currentPlaylistIndex]
                                    }
                                    onAnswer={onAnswer}
                                    isAtEnd={isAtEnd}
                                />
                            )}
                            {showFeedback && !isPlayingDecisionAnswer && (
                                <DecisionFeedback
                                    subject={subject}
                                    showAnswerVideo={showAnswerVideo}
                                    tryAgain={tryAgainDecisionSubject}
                                />
                            )}
                            {showStartFeedback && (
                                <StartFeedback
                                    startDecisionsSubjectVideo={startVideo}
                                />
                            )}

                            {!rerender && (
                                <ReactJWPlayer
                                    className={classes('video-item', {
                                        'decision-video-item':
                                            subject.video_decisions,
                                    })}
                                    image={subject.thumb}
                                    playerId={subject.slug || 'video-player'}
                                    playerScript={videoPlayerUrl}
                                    licenseKey="tbMPLx6pAOjLHsSbrqGJdPVygT90S8O71WG28+3dDJA="
                                    playlist={playlist}
                                    width="100%"
                                    aspectratio="16:9"
                                    responsive={true}
                                    isAutoPlay={false}
                                    onSetupError={onSetupError}
                                    onError={onError}
                                    allowFullscreen={false}
                                    onVideoLoad={resetWatchTracking}
                                    onPlay={() => {
                                        logEvent(
                                            subject,
                                            CONSTANTS.EVENTS.VIDEO_START,
                                            token
                                        );
                                        setIsPaused(false);
                                        //subject.isStarted();
                                    }}
                                    onResume={() => {
                                        setIsPaused(false);
                                    }}
                                    onPause={() => {
                                        setIsPaused(true);
                                    }}
                                    onReady={() => {
                                        setCaptionsLanguage();
                                        setIsReady(true);
                                        applyPlayerA11y();
                                        setShowStartFeedback(
                                            subject.type === 3 &&
                                                !isAtEnd &&
                                                !showFeedback &&
                                                !isPlayingDecisionAnswer &&
                                                !subject.hasBeenTried
                                        );
                                        if (isPlayingDecisionAnswer) {
                                            window.jwplayer().play();
                                        }
                                    }}
                                    onSeek={handleSeek}
                                    onTime={handleTime}
                                    customProps={customProps}
                                />
                            )}
                        </FlexDiv>
                        {subject.type === 3 && isReady && (
                            <DecisionPlayerProgressbar
                                counter={counter}
                                totalLength={
                                    isPlayingDecisionAnswer
                                        ? subject.answerSubjectLength
                                        : videoDecisions &&
                                          videoDecisions[
                                              currentPlaylistIndex
                                          ] &&
                                          videoDecisions[currentPlaylistIndex]
                                              .longestDecisionTree
                                }
                            />
                        )}
                        {shouldShowQuestionOverlay() && (
                            <DecisionOverlayMobile
                                decisionQuestion={
                                    videoDecisions[currentPlaylistIndex]
                                }
                                onAnswer={onAnswer}
                                isAtEnd={isAtEnd}
                            />
                        )}
                        {showStartFeedback && (
                            <StartFeedbackMobile
                                startDecisionsSubjectVideo={startVideo}
                            />
                        )}
                        {showFeedback && (
                            <DecisionFeedbackMobile
                                subject={subject}
                                showAnswerVideo={showAnswerVideo}
                                tryAgain={tryAgainDecisionSubject}
                            />
                        )}
                    </>
                ) : null
            ) : (
                <div className="alert alert-danger">
                    {t(
                        'Your browser does not currently recognize any of the video formats available'
                    )}
                </div>
            );
        }
    }
);
